import type { AutopilotAgent, Server } from "@cloudserver/contracts";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowUpCircle, BellRing, Gauge, Server as ServerIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useProjectAlerts } from "../../api/alerts.js";
import { useAutopilotProject } from "../../api/autopilot.js";
import { useServers } from "../../api/servers.js";
import { useCanWrite } from "../../api/users.js";
import { ErrorMessage } from "../../ui/feedback.js";
import { dateTime } from "../../ui/format.js";
import { EmptyState, PageHeader } from "../../ui/layout.js";
import { Badge, Button, Select } from "../../ui/primitives.js";
import { DataTable } from "../../ui/table.js";
import { EnableDialog } from "../server/autopilot/enable-dialog.js";
import { HEALTH_TONE, percentText, STATUS_TONE, usageTone } from "../server/autopilot/format.js";
import {
  buildRows,
  containerProblems,
  loadPerCore,
  matches,
  MONITORING_FILTERS,
  severityKey,
  totals,
  worstFirst,
  type MonitoringFilter,
  type MonitoringRow,
} from "./rows.js";

/*
 * The project overview «Мониторинг» (docs/autopilot.md): every server with
 * its Autopilot state and latest numbers, worst first, with links to each
 * server's tab and alerts; «Включить» for servers without Autopilot. The
 * numbers are the ones the sweep keeps (one request for the whole page, no
 * hub call per server); the firing alerts come from the alerts list.
 */

/** Firing alerts are read a page at a time; more than this many pages is not a real project. */
const MAX_ALERT_PAGES = 10;

const TONE_TEXT = { ok: "", neutral: "text-muted", warn: "text-warn", danger: "text-danger" };
const dash = <span className="text-muted">—</span>;

/** A usage percentage coloured by the API's warning thresholds. */
function Percent({
  value,
  warn,
  critical,
}: {
  value: number | null | undefined;
  warn?: number;
  critical?: number;
}) {
  const { i18n } = useTranslation();
  if (value === null || value === undefined) return dash;
  const tone =
    warn !== undefined && critical !== undefined ? usageTone(value, warn, critical) : "ok";
  return (
    <span className={clsx("tabular-nums", TONE_TEXT[tone])}>
      {percentText(value, i18n.language)}
    </span>
  );
}

function Tile({
  label,
  value,
  tone = "neutral",
  active,
  onClick,
}: {
  label: string;
  value: number;
  tone?: "neutral" | "warn" | "danger";
  active?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span
        className={clsx(
          "text-2xl font-semibold tabular-nums",
          value > 0 && tone === "danger" && "text-danger",
          value > 0 && tone === "warn" && "text-warn",
        )}
      >
        {value}
      </span>
      {/* Smaller on phones: two tiles a row leave ~150 px, «Обновлений безопасности» needs more. */}
      <span className="text-xs text-muted max-sm:text-[0.7rem]">{label}</span>
    </>
  );
  const base = "flex min-w-0 flex-col gap-0.5 rounded-xl border bg-surface px-3 py-3 text-left";
  if (!onClick) return <div className={clsx(base, "border-line")}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        base,
        active ? "border-accent bg-accent-soft" : "border-line hover:bg-surface-2",
      )}
    >
      {body}
    </button>
  );
}

/** Firing alerts of the project, every page of them (usually one). */
function useFiringAlerts(projectId: string) {
  const query = useProjectAlerts(projectId, { status: "firing" });
  const pages = query.data?.pages.length ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && pages < MAX_ALERT_PAGES) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, pages]);
  const data = query.data;
  return useMemo(() => data?.pages.flatMap((p) => p.items), [data]);
}

export function MonitoringPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const canWrite = useCanWrite(projectId);
  const servers = useServers(projectId);
  const autopilot = useAutopilotProject(projectId);
  const firing = useFiringAlerts(projectId);
  const [text, setText] = useState("");
  const [filter, setFilter] = useState<MonitoringFilter>("all");
  const [enabling, setEnabling] = useState<{ server: Server; agent: AutopilotAgent | null } | null>(
    null,
  );

  const all = useMemo(
    () =>
      servers.data && autopilot.data
        ? worstFirst(buildRows(servers.data, autopilot.data.agents, firing ?? []))
        : undefined,
    [servers.data, autopilot.data, firing],
  );
  const rows = useMemo(() => all?.filter((r) => matches(r, text, filter)), [all, text, filter]);
  const sum = all ? totals(all) : null;
  const toggle = (next: MonitoringFilter) => setFilter((prev) => (prev === next ? "all" : next));

  const header = (
    <PageHeader
      title={t("monitoring.title")}
      intro={t("monitoring.intro")}
      help="monitoring"
      actions={
        <Link
          to="/projects/$projectId/alerts"
          params={{ projectId }}
          className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-line bg-surface px-4 text-sm font-medium text-text hover:bg-surface-2"
        >
          <BellRing className="size-4" aria-hidden />
          {t("monitoring.actions.alerts")}
        </Link>
      }
    />
  );

  if (servers.error || autopilot.error) {
    return (
      <>
        {header}
        <ErrorMessage error={servers.error ?? autopilot.error} />
      </>
    );
  }
  if (autopilot.data && !autopilot.data.available) {
    return (
      <>
        {header}
        <EmptyState
          icon={<Gauge className="size-6" aria-hidden />}
          title={t("autopilot.unavailable.title")}
        >
          {t("autopilot.unavailable.text")}
        </EmptyState>
      </>
    );
  }

  const enableButton = (row: MonitoringRow, update: boolean) =>
    canWrite && (
      <Button
        variant="secondary"
        className="min-h-8 px-3"
        icon={
          update ? (
            <ArrowUpCircle className="size-4" aria-hidden />
          ) : (
            <Gauge className="size-4" aria-hidden />
          )
        }
        aria-label={
          update
            ? t("monitoring.update.actionFor", { name: row.server.name })
            : t("monitoring.actions.enableFor", { name: row.server.name })
        }
        onClick={() => setEnabling({ server: row.server, agent: row.agent })}
      >
        {update ? t("monitoring.update.action") : t("monitoring.actions.enable")}
      </Button>
    );

  const cellIfReports = (row: MonitoringRow, content: () => ReactNode) =>
    row.agent?.summary ? content() : dash;

  return (
    <>
      {header}
      {sum && sum.servers > 0 && (
        <div
          // Six tiles in a row only from `xl`: narrower, «Обновлений безопасности» broke word by word.
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6"
          role="group"
          aria-label={t("monitoring.tiles.label")}
        >
          <Tile label={t("monitoring.tiles.watched", { total: sum.servers })} value={sum.watched} />
          <Tile
            label={t("monitoring.tiles.critical")}
            value={sum.critical}
            tone="danger"
            active={filter === "critical"}
            onClick={() => toggle("critical")}
          />
          <Tile
            label={t("monitoring.tiles.problems")}
            value={sum.critical + sum.warn}
            tone="warn"
            active={filter === "problems"}
            onClick={() => toggle("problems")}
          />
          <Tile label={t("monitoring.tiles.alerts")} value={sum.alerts} tone="warn" />
          <Tile
            label={t("monitoring.tiles.securityUpdates")}
            value={sum.securityUpdates}
            tone="warn"
            active={filter === "updates"}
            onClick={() => toggle("updates")}
          />
          <Tile
            label={t("monitoring.tiles.off")}
            value={sum.off}
            active={filter === "off"}
            onClick={() => toggle("off")}
          />
        </div>
      )}
      {sum && sum.agentUpdates > 0 && (
        <p className="text-sm text-muted">
          {t("monitoring.update.summary", { count: sum.agentUpdates })}
        </p>
      )}
      {all && all.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("monitoring.filter.placeholder")}
            aria-label={t("monitoring.filter.label")}
            className="min-h-9 w-full min-w-0 rounded-lg border border-line bg-surface px-3 text-sm text-text placeholder:text-muted focus:border-accent sm:w-72"
          />
          <Select
            label={t("monitoring.filter.state")}
            value={filter}
            onChange={(e) => setFilter(e.target.value as MonitoringFilter)}
          >
            {MONITORING_FILTERS.map((f) => (
              <option key={f} value={f}>
                {t(`monitoring.filter.options.${f}`)}
              </option>
            ))}
          </Select>
        </div>
      )}
      {rows && rows.length === 0 && all && all.length > 0 ? (
        <p className="rounded-xl border border-dashed border-line bg-surface px-4 py-8 text-center text-sm text-muted">
          {t("monitoring.filter.noMatch")}
        </p>
      ) : (
        <DataTable<MonitoringRow>
          label={t("monitoring.title")}
          rows={rows}
          rowKey={(r) => r.server.id}
          loading={servers.isPending || autopilot.isPending}
          empty={
            <EmptyState
              icon={<ServerIcon className="size-6" aria-hidden />}
              title={t("monitoring.empty.title")}
              help="monitoring"
            >
              {t("monitoring.empty.text")}
            </EmptyState>
          }
          columns={[
            {
              key: "server",
              header: t("monitoring.columns.server"),
              primary: true,
              sort: (r) => r.server.name.toLowerCase(),
              cell: (r) => (
                <div className="flex min-w-0 flex-col">
                  <Link
                    to="/projects/$projectId/servers/$serverId"
                    params={{ projectId, serverId: r.server.id }}
                    search={{ tab: "autopilot" }}
                    className="truncate font-medium hover:text-accent hover:underline"
                    title={r.server.name}
                  >
                    {r.server.name}
                  </Link>
                  <span className="truncate text-xs text-muted">
                    {t(`servers.status.${r.server.status}`)}
                  </span>
                </div>
              ),
            },
            {
              key: "state",
              header: t("monitoring.columns.state"),
              sort: severityKey,
              cell: (r) =>
                r.agent ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    {r.agent.status === "reporting" ? (
                      <Badge tone={HEALTH_TONE[r.agent.health]}>
                        {t(`autopilot.health.${r.agent.health}`)}
                      </Badge>
                    ) : (
                      <Badge tone={STATUS_TONE[r.agent.status]}>
                        {t(`autopilot.status.${r.agent.status}`)}
                      </Badge>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted">{t("monitoring.cells.off")}</span>
                    {enableButton(r, false)}
                  </div>
                ),
            },
            {
              key: "alerts",
              header: t("monitoring.columns.alerts"),
              align: "right",
              sort: (r) => r.alerts.critical * 1000 + r.alerts.total,
              cell: (r) =>
                r.alerts.total > 0 ? (
                  <Link
                    to="/projects/$projectId/servers/$serverId"
                    params={{ projectId, serverId: r.server.id }}
                    search={{ tab: "alerts" }}
                    className={clsx(
                      "font-medium hover:underline",
                      r.alerts.critical > 0 ? "text-danger" : "text-warn",
                    )}
                    aria-label={t("monitoring.cells.alertsOf", {
                      count: r.alerts.total,
                      name: r.server.name,
                    })}
                  >
                    {r.alerts.total}
                  </Link>
                ) : (
                  dash
                ),
            },
            {
              key: "cpu",
              header: t("monitoring.columns.cpu"),
              align: "right",
              sort: (r) => r.agent?.summary?.cpuPercent ?? -1,
              cell: (r) => cellIfReports(r, () => <Percent value={r.agent?.summary?.cpuPercent} />),
            },
            {
              key: "memory",
              header: t("monitoring.columns.memory"),
              align: "right",
              sort: (r) => r.agent?.summary?.memoryPercent ?? -1,
              // Thresholds as the API warns: memory 90 / 97 %, disk 85 / 95 %.
              cell: (r) =>
                cellIfReports(r, () => (
                  <Percent value={r.agent?.summary?.memoryPercent} warn={90} critical={97} />
                )),
            },
            {
              key: "disk",
              header: t("monitoring.columns.disk"),
              align: "right",
              sort: (r) => r.agent?.summary?.diskPercent ?? -1,
              cell: (r) =>
                cellIfReports(r, () => (
                  <Percent value={r.agent?.summary?.diskPercent} warn={85} critical={95} />
                )),
            },
            {
              key: "load",
              header: t("monitoring.columns.load"),
              align: "right",
              hideOnPhone: true,
              sort: (r) => loadPerCore(r) ?? -1,
              cell: (r) => {
                const load = r.agent?.summary?.load?.[0];
                const perCore = loadPerCore(r);
                if (load === undefined) return dash;
                const format = (v: number) =>
                  new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(v);
                return (
                  <span
                    className={clsx(
                      "tabular-nums",
                      perCore !== null && perCore >= 2 && "text-warn",
                    )}
                    title={
                      perCore === null
                        ? undefined
                        : t("monitoring.cells.loadPerCore", { load: format(perCore) })
                    }
                  >
                    {format(load)}
                  </span>
                );
              },
            },
            {
              key: "services",
              header: t("monitoring.columns.services"),
              sort: (r) => r.agent?.summary?.services?.failed ?? -1,
              cell: (r) => {
                const services = r.agent?.summary?.services;
                if (!services) return dash;
                return services.failed > 0 ? (
                  <span className="whitespace-nowrap text-danger">
                    {t("monitoring.cells.servicesFailed", { count: services.failed })}
                  </span>
                ) : (
                  <span className="text-muted">
                    {t("monitoring.cells.servicesOk", { total: services.total })}
                  </span>
                );
              },
            },
            {
              key: "containers",
              header: t("monitoring.columns.containers"),
              sort: (r) => containerProblems(r.agent),
              cell: (r) => {
                if (r.agent && !r.agent.containers) {
                  return (
                    <span className="whitespace-nowrap text-xs text-muted">
                      {t("monitoring.cells.containersOff")}
                    </span>
                  );
                }
                const c = r.agent?.summary?.containers;
                if (!c) return dash;
                const problems = containerProblems(r.agent);
                return (
                  <div className="flex flex-col">
                    <span className="tabular-nums">
                      {t("monitoring.cells.containers", { running: c.running, total: c.total })}
                    </span>
                    {problems > 0 && (
                      <span
                        className={clsx("text-xs", c.restarting > 0 ? "text-danger" : "text-warn")}
                      >
                        {t("monitoring.cells.containerProblems", { count: problems })}
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              key: "updates",
              header: t("monitoring.columns.updates"),
              sort: (r) =>
                (r.agent?.summary?.updates?.security ?? -1) * 10_000 +
                (r.agent?.summary?.updates?.total ?? 0),
              cell: (r) => {
                const u = r.agent?.summary?.updates;
                if (!u) return dash;
                if (u.total === 0)
                  return <span className="text-muted">{t("monitoring.cells.updatesNone")}</span>;
                return (
                  <div className="flex flex-col">
                    {u.security > 0 && (
                      <span className="whitespace-nowrap text-warn">
                        {t("monitoring.cells.updatesSecurity", { count: u.security })}
                      </span>
                    )}
                    <span className="text-xs text-muted">
                      {t("monitoring.cells.updatesTotal", { count: u.total })}
                    </span>
                  </div>
                );
              },
            },
            {
              key: "certificates",
              header: t("monitoring.columns.certificates"),
              hideOnPhone: true,
              sort: (r) => {
                const c = r.agent?.summary?.certificates;
                return c ? c.expired * 1000 + c.expiring : -1;
              },
              cell: (r) => {
                const c = r.agent?.summary?.certificates;
                if (!r.agent?.summary) return dash;
                if (!c)
                  return (
                    <span className="text-xs text-muted">
                      {t("monitoring.cells.certificatesNotCollected")}
                    </span>
                  );
                if (c.total === 0) return dash;
                return (
                  <div className="flex flex-col">
                    {c.expired > 0 && (
                      <span className="text-danger">
                        {t("monitoring.cells.certificatesExpired", { count: c.expired })}
                      </span>
                    )}
                    {c.expiring > 0 && (
                      <span className="text-warn">
                        {t("monitoring.cells.certificatesExpiring", { count: c.expiring })}
                      </span>
                    )}
                    {c.nextExpiresAt && c.expired === 0 && c.expiring === 0 && (
                      <span className="text-xs text-muted">
                        {t("monitoring.cells.certificatesNext", {
                          date: dateTime(c.nextExpiresAt, locale),
                        })}
                      </span>
                    )}
                  </div>
                );
              },
            },
            {
              key: "agent",
              header: t("monitoring.columns.agent"),
              sort: (r) => (r.agent?.updateAvailable ? 0 : 1),
              cell: (r) =>
                r.agent ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-muted">{r.agent.version ?? "—"}</span>
                    {r.agent.updateAvailable && (
                      <>
                        <Badge tone="warn">{t("monitoring.update.badge")}</Badge>
                        {enableButton(r, true)}
                      </>
                    )}
                  </div>
                ) : (
                  dash
                ),
            },
          ]}
        />
      )}
      {enabling && (
        <EnableDialog
          projectId={projectId}
          server={enabling.server}
          agent={enabling.agent}
          open
          onOpenChange={(open) => {
            if (!open) setEnabling(null);
          }}
        />
      )}
    </>
  );
}
